import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useAuth } from '../../context/AuthContext';
import { orEmptyIfForbidden,
  getMaterialUsages,
  getProjects,
  getServiceRequests,
  updateMaterialUsageBillingStatus,
} from '../../services/api';
import { can, formatMoney, formatQuantity, sumMoney } from '../../lib/inventory';

const BillableMaterialsPage = () => {
  const { user } = useAuth();
  const [usages, setUsages] = useState([]);
  const [projects, setProjects] = useState([]);
  const [requests, setRequests] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    client: '', dossier: '', technician: '', date_from: '', date_to: '', billing_status: 'to_bill',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        billing_status: filters.billing_status === 'all' ? undefined : filters.billing_status,
        date_from: filters.date_from || undefined,
        date_to: filters.date_to || undefined,
      };
      const [usageResponse, projectResponse, requestResponse] = await Promise.all([
        getMaterialUsages(params), orEmptyIfForbidden(getProjects()), orEmptyIfForbidden(getServiceRequests()),
      ]);
      setUsages(usageResponse.data || []);
      setProjects(projectResponse.data || []);
      setRequests(requestResponse.data || []);
      setSelected([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible de charger le matériel à facturer');
    } finally { setLoading(false); }
  }, [filters.billing_status, filters.date_from, filters.date_to]);

  useEffect(() => { load(); }, [load]);

  const dossierFor = (usage) => {
    if (usage.project_id) {
      const project = projects.find((item) => item.id === usage.project_id);
      return { type: 'Chantier', id: usage.project_id, client: project?.client_name || 'Client inconnu' };
    }
    const request = requests.find((item) => item.id === usage.service_request_id);
    const client = request ? [request.first_name, request.last_name].filter(Boolean).join(' ') : 'Client inconnu';
    return { type: 'Appel de service', id: usage.service_request_id, client };
  };

  const visible = usages.filter((usage) => {
    const dossier = dossierFor(usage);
    return dossier.client.toLowerCase().includes(filters.client.toLowerCase()) &&
      (dossier.type + ' ' + dossier.id).toLowerCase().includes(filters.dossier.toLowerCase()) &&
      (usage.technician_name || '').toLowerCase().includes(filters.technician.toLowerCase());
  });

  const groups = (() => {
    const grouped = new Map();
    visible.forEach((usage) => {
      const dossier = dossierFor(usage);
      const key = usage.project_id ? 'project:' + usage.project_id : 'service:' + usage.service_request_id;
      if (!grouped.has(key)) grouped.set(key, { ...dossier, usages: [] });
      grouped.get(key).usages.push(usage);
    });
    return Array.from(grouped.values());
  })();

  const toggle = (id) => setSelected((current) =>
    current.includes(id) ? current.filter((value) => value !== id) : [...current, id]);

  const changeStatus = async (status) => {
    if (!selected.length) return;
    try {
      await updateMaterialUsageBillingStatus({ usage_ids: selected, status });
      toast.success(status === 'billed' ? 'Matériel marqué comme facturé' : 'Matériel rouvert');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible de modifier la facturation');
    }
  };

  const manageable = can(user, 'billing.materials.manage');

  return <div className="space-y-6">
    <div>
      <h1 className="text-2xl font-bold text-[#1a3a5c]">Matériel à facturer</h1>
      <p className="text-slate-500">Ajustez la facturation selon le matériel réellement utilisé.</p>
    </div>

    <Card><CardContent className="p-4 grid md:grid-cols-3 gap-3">
      <Input placeholder="Client" value={filters.client}
        onChange={(e) => setFilters({ ...filters, client: e.target.value })} />
      <Input placeholder="Type ou numéro de dossier" value={filters.dossier}
        onChange={(e) => setFilters({ ...filters, dossier: e.target.value })} />
      <Input placeholder="Technicien" value={filters.technician}
        onChange={(e) => setFilters({ ...filters, technician: e.target.value })} />
      <Input type="date" aria-label="Date de début" value={filters.date_from}
        onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} />
      <Input type="date" aria-label="Date de fin" value={filters.date_to}
        onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} />
      <Select value={filters.billing_status} onValueChange={(value) => setFilters({ ...filters, billing_status: value })}>
        <SelectTrigger><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="to_bill">À facturer</SelectItem>
          <SelectItem value="billed">Facturé</SelectItem>
          <SelectItem value="all">Tous les statuts</SelectItem>
        </SelectContent>
      </Select>
    </CardContent></Card>

    {manageable && <div className="flex flex-wrap gap-3">
      <Button disabled={!selected.length} onClick={() => changeStatus('billed')}>Marquer facturé</Button>
      <Button variant="outline" disabled={!selected.length} onClick={() => changeStatus('to_bill')}>Rouvrir à facturer</Button>
      <span className="text-sm text-slate-500 self-center">{selected.length} ligne(s) sélectionnée(s)</span>
    </div>}

    {loading ? <p>Chargement…</p> : groups.length === 0 ? <p className="text-slate-500">Aucun matériel correspondant.</p> :
      groups.map((group) => <Card key={group.type + group.id}><CardContent className="p-0">
        <div className="p-4 border-b flex justify-between gap-4">
          <div><h2 className="font-semibold">{group.client}</h2>
            <p className="text-sm text-slate-500">{group.type} · {group.id}</p></div>
          <div className="text-right"><p className="text-sm text-slate-500">Total</p>
            <p className="text-lg font-bold">{formatMoney(sumMoney(group.usages.map((usage) => usage.subtotal)))}</p></div>
        </div>
        <div className="divide-y">{group.usages.map((usage) => <label key={usage.id}
          className="p-4 flex items-center gap-4 hover:bg-slate-50">
          {manageable && <input type="checkbox" checked={selected.includes(usage.id)} onChange={() => toggle(usage.id)} />}
          <div className="flex-1"><p className="font-medium">{usage.item_name}</p>
            <p className="text-sm text-slate-500">{formatQuantity(usage.quantity)} {usage.unit} × {formatMoney(usage.unit_sale_price)}
              {' · '}{usage.technician_name || 'Technicien'}</p></div>
          <span className={usage.billing_status === 'billed' ? 'text-emerald-700' : 'text-amber-700'}>
            {usage.billing_status === 'billed' ? 'Facturé' : 'À facturer'}</span>
          <strong>{formatMoney(usage.subtotal)}</strong>
        </label>)}</div>
      </CardContent></Card>)}
  </div>;
};

export default BillableMaterialsPage;
